import { z } from "zod";

/**
 * Provider-neutral routing policy.
 *
 * A step is classified from structured signals only (its kind, the tools it asks
 * for, whether it drives a computer, whether it asks for vectors). The class maps
 * to a tier, the tier maps to a route, and the owner may pin a class to one route
 * or pin the tier itself. Nothing here reads prose, so the same inputs always
 * produce the same plan.
 */

export const InferenceStepKindSchema = z.enum([
  "triage",
  "summarization",
  "deep_reasoning",
  "coding",
  "computer_control",
  "embeddings",
]);
export type InferenceStepKind = z.infer<typeof InferenceStepKindSchema>;

/** Cost/quality tiers a step class can be served by. */
export const InferenceTierSchema = z.enum(["economy", "frontier"]);
export type InferenceTier = z.infer<typeof InferenceTierSchema>;

/** What a route can serve. Chat routes cannot answer an embeddings step. */
export const InferenceCapabilitySchema = z.enum(["chat", "embeddings"]);
export type InferenceCapability = z.infer<typeof InferenceCapabilitySchema>;

/** The tier a class uses unless the owner pins another one. */
export const INFERENCE_DEFAULT_TIERS: Readonly<Record<InferenceStepKind, InferenceTier>> = {
  triage: "economy",
  summarization: "economy",
  deep_reasoning: "frontier",
  coding: "frontier",
  computer_control: "frontier",
  embeddings: "economy",
};

/** Tools that drive a screen, a browser or a desktop application. */
const COMPUTER_CONTROL_TOOLS = new Set([
  "computer_observe",
  "computer_act",
  "browser_navigate",
  "browser_snapshot",
  "browser_act",
  "open_path",
  "launch_app",
]);

/** Tools that read or change files and run commands. */
const CODING_TOOLS = new Set([
  "shell",
  "user_computer_shell",
  "list_files",
  "read_file",
  "write_file",
  "attach_file",
  "create_document",
]);

/** Tools that gather outside material before the model answers. */
const DEEP_REASONING_TOOLS = new Set([
  "web_search",
  "web_fetch",
  "cloud_agent_launch",
  "cloud_agent_reply",
  "task_catalog",
]);

/** Tools that read back existing material the model condenses. */
const SUMMARIZATION_TOOLS = new Set([
  "search_history",
  "read_history",
  "recall_memory",
  "scratchpad_list",
  "board_tickets",
  "ticket_get",
  "skill_read",
]);

export const InferenceStepSignalsSchema = z.object({
  /** The caller already knows what kind of step this is; it wins over inference. */
  kind: InferenceStepKindSchema.optional(),
  /** Tool names the step may call. Names, never prose. */
  tools: z.array(z.string().trim().min(1).max(200)).max(512).optional(),
  /** The step drives a computer. */
  computerUse: z.boolean().optional(),
  /** The step asks for vectors rather than text. */
  embedding: z.boolean().optional(),
});
export type InferenceStepSignals = z.infer<typeof InferenceStepSignalsSchema>;

/**
 * Classify a step. An explicit kind is the caller's own knowledge and wins.
 * Otherwise the flags and the tool names decide, most demanding first.
 */
export function classifyInferenceStep(signals: InferenceStepSignals): InferenceStepKind {
  if (signals.kind) return signals.kind;
  if (signals.embedding) return "embeddings";
  if (signals.computerUse) return "computer_control";
  const tools = signals.tools ?? [];
  if (tools.some((name) => COMPUTER_CONTROL_TOOLS.has(name))) return "computer_control";
  if (tools.some((name) => CODING_TOOLS.has(name))) return "coding";
  if (tools.some((name) => DEEP_REASONING_TOOLS.has(name))) return "deep_reasoning";
  if (tools.some((name) => SUMMARIZATION_TOOLS.has(name))) return "summarization";
  return "triage";
}

export const InferenceRouteSchema = z.object({
  /** Stable name the owner pins to. */
  id: z.string().trim().min(1).max(128),
  /** Connection provider that serves the route. */
  provider: z.string().trim().min(1).max(128),
  /** Model id the route runs. */
  modelId: z.string().trim().min(1).max(512),
  tier: InferenceTierSchema,
  capability: InferenceCapabilitySchema.default("chat"),
  /**
   * Processing region of the route, as an ISO 3166-1 alpha-2 code. A route with
   * no region never serves a workspace that requires one.
   */
  residency: z
    .string()
    .trim()
    .regex(/^[a-z]{2}$/, "Residency must be a lowercase two-letter region code")
    .nullable()
    .optional(),
  /** Connection that holds the credential for the route. */
  credentialId: z.string().trim().min(1).max(128).optional(),
  /** Endpoint of the route, for OpenAI-compatible providers. */
  baseUrl: z.string().trim().min(1).max(2048).optional(),
  /** Lower runs first among the routes of one tier. */
  priority: z.number().int().min(0).max(10_000).optional(),
});
export type InferenceRoute = z.infer<typeof InferenceRouteSchema>;

export const InferenceResidencyPolicySchema = z.object({
  /** Region the workspace's traffic must stay in. */
  region: z
    .string()
    .trim()
    .regex(/^[a-z]{2}$/, "Residency must be a lowercase two-letter region code"),
  /** The owner explicitly allows traffic to leave the region. */
  allowCrossBorder: z.boolean().optional(),
});
export type InferenceResidencyPolicy = z.infer<typeof InferenceResidencyPolicySchema>;

export const InferenceTierOverridesSchema = z
  .object({
    triage: InferenceTierSchema,
    summarization: InferenceTierSchema,
    deep_reasoning: InferenceTierSchema,
    coding: InferenceTierSchema,
    computer_control: InferenceTierSchema,
    embeddings: InferenceTierSchema,
  })
  .partial();

export const InferencePinOverridesSchema = z
  .object({
    triage: z.string().trim().min(1).max(128),
    summarization: z.string().trim().min(1).max(128),
    deep_reasoning: z.string().trim().min(1).max(128),
    coding: z.string().trim().min(1).max(128),
    computer_control: z.string().trim().min(1).max(128),
    embeddings: z.string().trim().min(1).max(128),
  })
  .partial();

export const InferenceRoutingPolicySchema = z.object({
  /** The routes this workspace may use. */
  routes: z.array(InferenceRouteSchema).max(256),
  /** Tier per step class. A missing class keeps its default tier. */
  tiers: InferenceTierOverridesSchema.optional(),
  /** Route pinned to a step class. A pinned route is the only candidate. */
  pins: InferencePinOverridesSchema.optional(),
  /** Residency requirement enforced on every route the policy picks. */
  residency: InferenceResidencyPolicySchema.optional(),
  /** Whether a failed attempt may fall through to the next route. Defaults to true. */
  fallback: z.boolean().optional(),
});
export type InferenceRoutingPolicy = z.infer<typeof InferenceRoutingPolicySchema>;

/** The stored shape of a routing policy, as it is kept in the database. */
export type StoredInferencePolicy = {
  residency?: string | null;
  allowCrossBorder?: boolean | null;
  routes?: unknown;
  tiers?: unknown;
  pins?: unknown;
};

/**
 * The policy a stored row describes. A row that cannot be read routes nothing,
 * so a broken setting never sends a run to an unexpected model.
 */
export function inferencePolicyFromStored(
  stored: StoredInferencePolicy | null | undefined,
): InferenceRoutingPolicy {
  if (!stored) return { routes: [] };
  const parsed = InferenceRoutingPolicySchema.safeParse({
    routes: stored.routes ?? [],
    ...(stored.tiers ? { tiers: stored.tiers } : {}),
    ...(stored.pins ? { pins: stored.pins } : {}),
    ...(stored.residency
      ? {
          residency: {
            region: stored.residency,
            allowCrossBorder: stored.allowCrossBorder ?? false,
          },
        }
      : {}),
  });
  return parsed.success ? parsed.data : { routes: [] };
}

/** Which policy a request is about: the account default or the space's exception. */
export const InferencePolicyScopeSchema = z.enum(["account", "space"]);
export type InferencePolicyScope = z.infer<typeof InferencePolicyScopeSchema>;

/** What a policy is saved with. */
export const InferencePolicyInputSchema = z.object({
  scope: InferencePolicyScopeSchema.default("space"),
  /** Region the workspace's traffic must stay in. */
  residency: z
    .string()
    .trim()
    .regex(/^[a-z]{2}$/, "Residency must be a lowercase two-letter region code")
    .nullable()
    .optional(),
  /** The owner explicitly allows traffic to leave the region. */
  allowCrossBorder: z.boolean().optional(),
  /** The routes the policy may use. */
  routes: z.array(InferenceRouteSchema).max(256).optional(),
  /** The tier each step class uses. */
  tiers: InferenceTierOverridesSchema.optional(),
  /** The route each step class is pinned to. */
  pins: InferencePinOverridesSchema.optional(),
});
export type InferencePolicyInput = z.infer<typeof InferencePolicyInputSchema>;

/** The policy that applies to a scope, and where it comes from. */
export const InferencePolicyViewSchema = z.object({
  scope: InferencePolicyScopeSchema,
  /** Where the effective policy comes from. */
  source: z.enum(["space", "organization", "operator"]),
  /** The policy that applies right now. */
  effective: InferenceRoutingPolicySchema,
  /** The scope's own policy, when it has one. */
  own: InferenceRoutingPolicySchema.nullable(),
  /**
   * Whether the reader may change this scope's policy: an organization admin for
   * the account default, a space manager for the space's exception. A member can
   * read the policy either way.
   */
  canManage: z.boolean(),
});
export type InferencePolicyView = z.infer<typeof InferencePolicyViewSchema>;

export type InferenceRoutingFailureCode =
  | "no-route"
  | "residency-blocked"
  | "unknown-pin"
  | "invalid-policy"
  /** The policy could not be read, so nothing about it may be assumed. */
  | "policy-unavailable";

/** A routing decision the workspace cannot satisfy, with a machine-readable code. */
export class InferenceRoutingError extends Error {
  readonly code: InferenceRoutingFailureCode;
  readonly step: InferenceStepKind | undefined;
  readonly tier: InferenceTier | undefined;

  constructor(
    code: InferenceRoutingFailureCode,
    message: string,
    details?: { step?: InferenceStepKind; tier?: InferenceTier },
  ) {
    super(message);
    this.name = "InferenceRoutingError";
    this.code = code;
    this.step = details?.step;
    this.tier = details?.tier;
  }
}

export function isInferenceRoutingError(error: unknown): error is InferenceRoutingError {
  return error instanceof InferenceRoutingError;
}

/** The ordered attempts a step may use: the first is primary, the rest fallbacks. */
export type InferenceRoutePlan = {
  step: InferenceStepKind;
  tier: InferenceTier;
  routes: InferenceRoute[];
  source: "pin" | "tier";
};

/**
 * Whether a route is bound to a concrete connection.
 *
 * A region tag only describes traffic when the route names where the traffic
 * goes: the connection it runs on, or the endpoint it calls. A route that names
 * neither carries no residency claim.
 */
export function routeBindsConnection(route: InferenceRoute): boolean {
  return Boolean(route.credentialId || route.baseUrl);
}

/**
 * Whether a route may serve a workspace with this residency policy. A workspace
 * that requires a region only accepts routes tagged with it and bound to a
 * connection, unless the owner explicitly opted out of in-country routing.
 */
export function routeSatisfiesResidency(
  route: InferenceRoute,
  residency: InferenceResidencyPolicy | undefined,
): boolean {
  if (!residency) return true;
  if (residency.allowCrossBorder) return true;
  if (!routeBindsConnection(route)) return false;
  return route.residency === residency.region;
}

function capabilityFor(step: InferenceStepKind): InferenceCapability {
  return step === "embeddings" ? "embeddings" : "chat";
}

function routeOrder(a: InferenceRoute, b: InferenceRoute): number {
  const priority = (a.priority ?? 0) - (b.priority ?? 0);
  if (priority !== 0) return priority;
  return a.id.localeCompare(b.id);
}

/**
 * Plan the routes for a step. Throws a typed failure when the owner's policy has
 * nothing the step may use, so a caller never falls back to an arbitrary model.
 */
export function planInferenceRoute(
  policy: InferenceRoutingPolicy,
  signals: InferenceStepSignals,
): InferenceRoutePlan {
  const step = classifyInferenceStep(signals);
  const tier = policy.tiers?.[step] ?? INFERENCE_DEFAULT_TIERS[step];
  const pinnedId = policy.pins?.[step];
  if (pinnedId) {
    const pinned = policy.routes.find((route) => route.id === pinnedId);
    if (!pinned) {
      throw new InferenceRoutingError(
        "unknown-pin",
        `No route named "${pinnedId}" is available for this workspace.`,
        { step, tier },
      );
    }
    if (!routeSatisfiesResidency(pinned, policy.residency)) {
      throw new InferenceRoutingError(
        "residency-blocked",
        `The pinned route for ${step} does not serve the workspace's region.`,
        { step, tier: pinned.tier },
      );
    }
    return { step, tier: pinned.tier, routes: [pinned], source: "pin" };
  }
  const capability = capabilityFor(step);
  const tierRoutes = policy.routes.filter(
    (route) => route.tier === tier && route.capability === capability,
  );
  if (tierRoutes.length === 0) {
    throw new InferenceRoutingError("no-route", `No ${tier} route is available for ${step}.`, {
      step,
      tier,
    });
  }
  const eligible = tierRoutes.filter((route) => routeSatisfiesResidency(route, policy.residency));
  if (eligible.length === 0) {
    throw new InferenceRoutingError(
      "residency-blocked",
      `No ${tier} route for ${step} serves the workspace's region.`,
      { step, tier },
    );
  }
  const ordered = [...eligible].sort(routeOrder);
  return {
    step,
    tier,
    routes: policy.fallback === false ? ordered.slice(0, 1) : ordered,
    source: "tier",
  };
}
